import * as duckdb from "@duckdb/duckdb-wasm";

const SELECT_COLUMNS = [
  "parcel_id",
  "prop_id",
  "geo_id",
  "owner_name",
  "name_care",
  "situs_addr",
  "mail_addr",
  "county",
  "fips",
  "tax_year",
  "land_value",
  "imp_value",
  "mkt_value",
  "legal_desc",
  "date_acq",
  "source"
];

function sqlLiteral(value) {
  if (value === null || value === undefined) {
    return "NULL";
  }
  return `'${String(value).replace(/'/g, "''")}'`;
}

function rowToPlainObject(row) {
  const raw = typeof row.toJSON === "function" ? row.toJSON() : row;
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    out[key] = typeof value === "bigint" ? Number(value) : value;
  }
  return out;
}

function toRows(table) {
  if (!table || typeof table.toArray !== "function") {
    return [];
  }
  return table.toArray().map(rowToPlainObject);
}

function trimTrailingSlash(url) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

export function normalizeFips(input) {
  if (input === null || input === undefined) {
    return null;
  }
  const digits = String(input).replace(/\D/g, "");
  if (!digits) {
    return null;
  }
  return digits.padStart(5, "0");
}

export class ParcelStore {
  constructor(config) {
    this.config = config;
    this.bundle = null;
    this.db = null;
    this.conn = null;
    this.worker = null;
    this.manifest = null;
    this.loadedFiles = new Map();
    this.initPromise = null;
  }

  async init() {
    if (!this.initPromise) {
      this.initPromise = this.#initInternal();
    }
    return this.initPromise;
  }

  async #initInternal() {
    if (!this.config?.baseUrl || !this.config?.manifestUrl) {
      throw new Error("Missing attributes.baseUrl or attributes.manifestUrl in config.");
    }

    this.bundle = await duckdb.selectBundle(duckdb.getJsDelivrBundles());
    const workerSource = `importScripts("${this.bundle.mainWorker}");`;
    const workerUrl = URL.createObjectURL(
      new Blob([workerSource], { type: "text/javascript" })
    );
    this.worker = new Worker(workerUrl);
    this.db = new duckdb.AsyncDuckDB(new duckdb.ConsoleLogger(), this.worker);
    await this.db.instantiate(this.bundle.mainModule, this.bundle.pthreadWorker);
    URL.revokeObjectURL(workerUrl);
    this.conn = await this.db.connect();

    const manifestResponse = await fetch(this.config.manifestUrl, { cache: "no-store" });
    if (!manifestResponse.ok) {
      throw new Error(`Could not load manifest at ${this.config.manifestUrl}`);
    }
    this.manifest = await manifestResponse.json();
  }

  #normalizeCountyPaths(county) {
    if (Array.isArray(county?.paths)) {
      return county.paths
        .map((entry) => {
          if (!entry) {
            return null;
          }
          if (typeof entry === "string") {
            return entry;
          }
          if (typeof entry.path === "string") {
            return entry.path;
          }
          return null;
        })
        .filter(Boolean);
    }

    if (typeof county?.path === "string") {
      return [county.path];
    }

    return [];
  }

  async #ensureCountyLoaded(fips) {
    const county = this.manifest?.counties?.[fips];
    const paths = this.#normalizeCountyPaths(county);
    if (!Array.isArray(paths) || paths.length === 0) {
      throw new Error(`County FIPS ${fips} not found in manifest.`);
    }
    if (this.loadedFiles.has(fips)) {
      return this.loadedFiles.get(fips);
    }

    const resultPaths = [];

    for (const [index, relPath] of paths.entries()) {
      const url = `${trimTrailingSlash(this.config.baseUrl)}/${relPath}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Could not fetch ${url}`);
      }

      const bytes = new Uint8Array(await response.arrayBuffer());
      const virtualPath = `county_${fips}_${index}.parquet`;
      await this.db.registerFileBuffer(virtualPath, bytes);
      resultPaths.push(virtualPath);
    }

    if (resultPaths.length === 0) {
      throw new Error(`County FIPS ${fips} has no available parquet file data.`);
    }

    this.loadedFiles.set(fips, resultPaths);
    return resultPaths;
  }

  async lookupParcel(parcelId, inputFips) {
    await this.init();

    if (!parcelId) {
      return null;
    }

    const parsedFips =
      normalizeFips(inputFips) ?? normalizeFips(String(parcelId).split(":")[0] ?? null);
    if (!parsedFips) {
      throw new Error("Could not determine county FIPS for this parcel.");
    }

    const files = await this.#ensureCountyLoaded(parsedFips);

    const selects = files.map((filePath) => `
      SELECT ${SELECT_COLUMNS.join(", ")}
      FROM read_parquet(${sqlLiteral(filePath)})
      WHERE parcel_id = ${sqlLiteral(parcelId)}
    `);
    const sql = `
      SELECT ${SELECT_COLUMNS.join(", ")}
      FROM (
        ${selects.join(" UNION ALL ")}
      ) AS parcel_matches
      LIMIT 1
    `;

    const table = await this.conn.query(sql);
    const rows = toRows(table);
    return rows[0] ?? null;
  }
}
